import React, { useCallback, useEffect, useState } from "react";
import { Plus, Search, KanbanSquare, Users, Contact2, Archive, Upload } from "lucide-react";
import { PageHeader, Button, Input, Select, Card, CardContent, StatusBadge, LoadingBlock, EmptyState, toast, ConfirmDialog } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { STAGE_META, STAGE_ORDER, PRIORITIES, PRIORITY_TONE, fmtDate } from "./crm/constants";
import { LeadProfile } from "./crm/LeadProfile";
import { LeadForm } from "./crm/LeadForm";
import { ContactForm } from "./crm/ContactForm";
import { ImportContacts } from "./crm/ImportContacts";

type Tab = "pipeline" | "leads" | "contacts";
const TABS: { key: Tab; label: string; icon: typeof Users }[] = [
  { key: "pipeline", label: "Pipeline", icon: KanbanSquare },
  { key: "leads", label: "Leads", icon: Users },
  { key: "contacts", label: "Contacts", icon: Contact2 },
];

function PipelineBoard({ onOpen, reloadKey }: { onOpen: (id: string) => void; reloadKey: number }) {
  const [board, setBoard] = useState<any>(null);
  useEffect(() => { api.crmBoard().then(setBoard).catch((e) => toast.error(formatApiError(e))); }, [reloadKey]);
  if (!board) return <LoadingBlock rows={4} />;
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" data-testid="pipeline-board">
      {STAGE_ORDER.map((s) => {
        const items = board.columns[s] ?? [];
        return (
          <div key={s} className="w-64 shrink-0" data-testid={`pipeline-col-${s}`}>
            <div className="mb-2 flex items-center justify-between px-1">
              <span className="text-sm font-semibold text-slate-700">{STAGE_META[s].label}</span>
              <StatusBadge tone={STAGE_META[s].tone}>{items.length}</StatusBadge>
            </div>
            <div className="space-y-2">
              {items.map((l: any) => (
                <button key={l.id} onClick={() => onOpen(l.id)} data-testid={`pipeline-card-${l.id}`}
                  className="w-full rounded-lg border border-slate-200 bg-white p-3 text-left transition-all hover:border-blue-300 hover:shadow-sm">
                  <p className="truncate text-sm font-medium text-slate-900">{l.contact_name}</p>
                  <p className="truncate text-xs text-slate-500">{l.requested_service ?? "—"}</p>
                  <div className="mt-1.5 flex items-center justify-between">
                    <StatusBadge tone={PRIORITY_TONE[l.priority]}>{l.priority}</StatusBadge>
                    <span className="text-xs font-semibold text-slate-600">${Number(l.estimated_value ?? 0).toLocaleString()}</span>
                  </div>
                </button>
              ))}
              {items.length === 0 && <p className="px-1 py-3 text-xs text-slate-400">No leads</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LeadsTable({ onOpen, reloadKey }: { onOpen: (id: string) => void; reloadKey: number }) {
  const [rows, setRows] = useState<any[] | null>(null);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const load = useCallback(() => {
    const params: Record<string, string> = {};
    if (q.trim()) params.q = q.trim();
    if (stage !== "all") params.stage = stage;
    api.crmLeads(params).then(setRows).catch((e) => toast.error(formatApiError(e)));
  }, [q, stage]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, reloadKey]);

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…" className="pl-9" data-testid="leads-search" /></div>
        <div className="w-48"><Select value={stage} onChange={setStage} options={[{ value: "all", label: "All stages" }, ...STAGE_ORDER.map((s) => ({ value: s, label: STAGE_META[s].label }))]} testid="leads-stage-filter" /></div>
      </div>
      {rows === null ? <LoadingBlock rows={4} /> : rows.length === 0 ? (
        <EmptyState icon={Users} title="No leads" description="Create a lead to start tracking your pipeline." />
      ) : (
        <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="leads-table">
          {rows.map((l) => (
            <button key={l.id} onClick={() => onOpen(l.id)} className="flex w-full items-center justify-between px-5 py-3.5 text-left hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{l.contact_name}</p>
                <p className="truncate text-xs text-slate-500">{l.requested_service ?? "—"} · {l.contact_phone ?? "no phone"}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge tone={PRIORITY_TONE[l.priority]}>{l.priority}</StatusBadge>
                <StatusBadge tone={STAGE_META[l.stage].tone}>{STAGE_META[l.stage].label}</StatusBadge>
              </div>
            </button>
          ))}
        </CardContent></Card>
      )}
    </div>
  );
}

function ContactsTable({ onEdit, reloadKey }: { onEdit: (c: any) => void; reloadKey: number }) {
  const [rows, setRows] = useState<any[] | null>(null);
  const [q, setQ] = useState("");
  const [toArchive, setToArchive] = useState<string | null>(null);
  const load = useCallback(() => {
    const params: Record<string, string> = {};
    if (q.trim()) params.q = q.trim();
    api.crmContacts(params).then(setRows).catch((e) => toast.error(formatApiError(e)));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, reloadKey]);

  const archive = async (id: string) => { try { await api.crmContactArchive(id); toast.success("Archived"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-3">
      <div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts…" className="pl-9" data-testid="contacts-search" /></div>
      {rows === null ? <LoadingBlock rows={4} /> : rows.length === 0 ? (
        <EmptyState icon={Contact2} title="No contacts" description="Add a contact or create a lead to build your contact list." />
      ) : (
        <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="contacts-table">
          {rows.map((c) => (
            <div key={c.id} className="flex items-center justify-between px-5 py-3.5" data-testid={`contact-row-${c.id}`}>
              <button onClick={() => onEdit(c)} className="min-w-0 flex-1 text-left" data-testid={`contact-edit-${c.id}`}>
                <p className="truncate text-sm font-medium text-slate-900">{c.first_name} {c.last_name ?? ""}</p>
                <p className="truncate text-xs text-slate-500">{c.phone ?? "no phone"}{c.email ? ` · ${c.email}` : ""}</p>
              </button>
              <div className="flex items-center gap-2">
                {c.do_not_call && <StatusBadge tone="red">DNC</StatusBadge>}
                {c.do_not_text && <StatusBadge tone="red">DNT</StatusBadge>}
                <Button variant="ghost" size="sm" onClick={() => setToArchive(c.id)} data-testid={`contact-archive-${c.id}`}><Archive className="h-4 w-4 text-slate-400" /></Button>
              </div>
            </div>
          ))}
        </CardContent></Card>
      )}
      <ConfirmDialog open={!!toArchive} onOpenChange={(o) => !o && setToArchive(null)} title="Archive contact?"
        description="Archived contacts are hidden from the main list." confirmLabel="Archive" destructive
        onConfirm={() => { if (toArchive) archive(toArchive); setToArchive(null); }} testid="contact-archive-confirm" />
    </div>
  );
}

export default function Leads() {
  const [tab, setTab] = useState<Tab>("pipeline");
  const [reloadKey, setReloadKey] = useState(0);
  const [openLead, setOpenLead] = useState<string | null>(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editContact, setEditContact] = useState<any>(null);
  const reload = () => setReloadKey((k) => k + 1);

  return (
    <div data-testid="page-leads" className="space-y-6">
      <PageHeader title="Contacts & Leads" description="Your prospects, their pipeline stage and activity." testid="leads-header"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-contacts-btn"><Upload className="h-4 w-4" /> Import</Button>
            <Button variant="outline" onClick={() => { setEditContact(null); setShowContactForm(true); }} data-testid="add-contact-btn"><Plus className="h-4 w-4" /> Add contact</Button>
            <Button onClick={() => setShowLeadForm(true)} data-testid="add-lead-btn"><Plus className="h-4 w-4" /> Add lead</Button>
          </div>
        } />

      <div className="flex gap-1 border-b border-slate-200" data-testid="crm-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`crm-tab-${key}`}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "pipeline" && <PipelineBoard onOpen={setOpenLead} reloadKey={reloadKey} />}
      {tab === "leads" && <LeadsTable onOpen={setOpenLead} reloadKey={reloadKey} />}
      {tab === "contacts" && <ContactsTable onEdit={(c) => { setEditContact(c); setShowContactForm(true); }} reloadKey={reloadKey} />}

      {openLead && <LeadProfile leadId={openLead} onClose={() => setOpenLead(null)} onChanged={reload} />}
      {showLeadForm && <LeadForm onClose={() => setShowLeadForm(false)} onSaved={() => { setShowLeadForm(false); reload(); }} />}
      {showContactForm && <ContactForm contact={editContact} onClose={() => setShowContactForm(false)} onSaved={() => { setShowContactForm(false); reload(); }} />}
      {showImport && <ImportContacts onClose={() => setShowImport(false)} onImported={reload} />}
    </div>
  );
}
